<template>
    <div class="dashboard">
      <!-- Backdrop for mobile -->
      <div v-if="showMobileSidebar" class="backdrop" @click="closeSidebar"></div>
  
      <!-- Sidebar -->
      <aside
        :class="[
          'sidebar',
          { collapsed: isCollapsed, 'sidebar-mobile': isMobile, 'sidebar-open': showMobileSidebar }
        ]"
      >
        <!-- Header -->
        <div v-if="!isMobile" class="sidebar-header">
          <a href="/">
            <img v-if="!isCollapsed" src="../assets/logo/appLogo.svg" alt="" />
          </a>
  
          <div class="toggle-container">
            <button @click="toggleSidebar" class="toggle-btn">
              <component :is="isCollapsed ? ArrowRight : ArrowLeft" />
            </button>
          </div>
        </div>
  
        <nav class="sidebar-nav" v-if="menuItems.length">
          <ul>
            <li v-for="item in menuItems" :key="item.name" class="nav-item">
              <template v-if="item.children && item.children.length">
                <div class="menu-group">
                  <div
                    class="nav-link parent-link"
                    @click="toggleMenu(item.name)"
                  >
                    <div class="left">
                      <span class="icon">
                        <component :is="item.icon" />
                      </span>
                      <span v-if="!isCollapsed || isMobile" class="label">
                        {{ item.name }}
                      </span>
                    </div>

                    <ChevronDown
                      v-if="!isCollapsed || isMobile"
                      class="chevron"
                      :class="{ open: openMenus[item.name] }"
                    />
                  </div>

                  <transition name="submenu">
                    <ul
                      v-if="openMenus[item.name] && (!isCollapsed || isMobile)"
                      class="submenu"
                    >
                      <li v-for="child in item.children" :key="child.name">
                        <router-link
                          :to="child.path"
                          class="nav-link submenu-link"
                          @click="closeSidebar"
                        >
                          <span class="label">{{ child.name }}</span>
                        </router-link>
                      </li>
                    </ul>
                  </transition>
                </div>
              </template>

              <template v-else>
                <router-link
                  :to="item.path"
                  class="nav-link"
                  @click="closeSidebar"
                >
                  <span class="icon">
                    <component :is="item.icon" />
                  </span>
                  <span v-if="!isCollapsed || isMobile" class="label">
                    {{ item.name }}
                  </span>
                </router-link>
              </template>
            </li>
          </ul>

          <ul>
            <li v-for="item in logout" :key="item.name" class="nav-item">
              <button class="nav-link" @click="item.action">
                <span class="icon">
                  <component :is="item.icon" />
                </span>
                <span v-if="!isCollapsed || isMobile" class="label">
                  {{ item.name }}
                </span>
              </button>
            </li>
          </ul>
        </nav>

      </aside>
  
      <!-- Mobile toggle -->
      <button v-if="isMobile" class="mobile-toggle" @click="toggleSidebar">
        <component :is="showMobileSidebar ? ArrowLeft : ArrowRight" />
      </button>
  
      <!-- Main content -->
      <main class="main-content">
        <router-view />
      </main>
    </div>
  </template>  
<script setup>
/* eslint-disable */ 
import { 
  ArrowLeft, 
  ArrowRight, 
  Clock, 
  House, 
  UsersRound, 
  CreditCard, 
  Image, 
  Star, 
  Layers, 
  Calendar, 
  Dumbbell, 
  BookOpen, 
  GalleryHorizontal, 
  Info, 
  Users, 
  Trophy, 
  Newspaper, 
  LifeBuoy, 
  FileQuestion,
  ClipboardList,
  ChevronDown,
  LogOut  } from "lucide-vue-next";
import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";
import axios from "axios";
import router from "@/router";

const isCollapsed = ref(false);
const isMobile = ref(false);
const showMobileSidebar = ref(false);
const created_by = ref(localStorage.getItem('userid'));
const user_role = ref(localStorage.getItem('user_role'));
const openMenus = ref({});
const dynamicMenu = ref([]);

const normalizedRole = computed(() => String(user_role.value || "").toLowerCase());
const fallbackRoleMenu = computed(() =>
  normalizedRole.value === "subadmin" ? subadminMenu : memberMenu
);
const menuItems = computed(() => {
  if (normalizedRole.value === "admin") {
    return adminMenu;
  }

  return dynamicMenu.value.length ? dynamicMenu.value : fallbackRoleMenu.value;
});

function toggleSidebar() {
    if (isMobile.value) {
        showMobileSidebar.value = !showMobileSidebar.value;
    } else {
        isCollapsed.value = !isCollapsed.value;
    }
}

function closeSidebar() {
    showMobileSidebar.value = false;
}

function syncAuthState() {
  created_by.value = localStorage.getItem("userid");
  user_role.value = localStorage.getItem("user_role");
}

// Watch screen size
function handleResize() {
    isMobile.value = window.innerWidth <= 768;
    if (!isMobile.value) {
        showMobileSidebar.value = false;
    }
}

onMounted(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("storage", syncAuthState);
    syncAuthState();
    loadMenuByRole();
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("storage", syncAuthState);
});

const toggleMenu = (name) => {
  openMenus.value[name] = !openMenus.value[name];
};

function normalizeRoutePath(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function routeIconByName(name) {
  const key = String(name || "").toLowerCase();

  if (key.includes("dashboard")) return House;
  if (key.includes("application")) return BookOpen;
  if (key.includes("payment") || key.includes("fee") || key.includes("subscription")) return CreditCard;
  if (key.includes("booking") || key.includes("court")) return Clock;
  if (key.includes("member")) return UsersRound;
  if (key.includes("event") || key.includes("tournament")) return Trophy;
  if (key.includes("content") || key.includes("banner") || key.includes("logo") || key.includes("gallery") || key.includes("photo") || key.includes("media")) return Image;
  if (key.includes("policy") || key.includes("legal") || key.includes("terms") || key.includes("privacy") || key.includes("about") || key.includes("faq")) return Info;
  if (key.includes("audit") || key.includes("log")) return ClipboardList;
  if (key.includes("profile")) return UsersRound;

  return ClipboardList;
}

function buildMenuTree(routes) {
  const normalized = (routes || []).map((route) => ({
    routeId: route.RouteId ?? route.routeId ?? route.id,
    name: route.RouteName ?? route.routeName ?? route.name ?? "Untitled",
    path: normalizeRoutePath(route.RoutePath ?? route.routePath ?? route.path),
    parentRouteId: route.ParentRouteId ?? route.parentRouteId ?? null,
    sortOrder: Number(route.SortOrder ?? route.sortOrder ?? 0) || 0,
    icon: routeIconByName(route.RouteName ?? route.routeName ?? route.name)
  }));

  const nodes = new Map();
  const roots = [];

  normalized.forEach((item) => {
    nodes.set(item.routeId, { ...item, children: [] });
  });

  nodes.forEach((node) => {
    if (node.parentRouteId && nodes.has(node.parentRouteId)) {
      nodes.get(node.parentRouteId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items) => {
    items.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    items.forEach((item) => {
      if (item.children.length) {
        sortNodes(item.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
}

async function loadMenuByRole() {
  syncAuthState();

  if (normalizedRole.value === "admin") {
    dynamicMenu.value = [];
    return;
  }

  try {
    console.log("Loading sidebar routes for role:", normalizedRole.value);
    const response = await axios.get("/role_routes_by_role.php", {
      params: {
        role: normalizedRole.value,
        userid: created_by.value
      }
    });

    const routes = response?.data?.data ?? [];
    dynamicMenu.value = buildMenuTree(routes);
  } catch (error) {
    console.error("Failed to load role routes:", error);
    dynamicMenu.value = [];
  }
}

watch(
  () => user_role.value,
  () => {
    loadMenuByRole();
  }
);


// methods


// Example menu items with icons

const adminMenu = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: House
  },

  {
    name: "User Management",
    icon: UsersRound,
    children: [
      { name: "Add Members", path: "/create-members" },
      { name: "Members", path: "/members" },
      { name: "Applications", path: "/applications" },
      { name: "Event Participants", path: "/event-participants" },
      { name: "Tournament Participants", path: "/tournament-participants" }
    ]
  },

  {
    name: "Finance",
    icon: CreditCard,
    children: [
      { name: "Payments", path: "/payments" },
      { name: "Fees Setup", path: "/fees-list" },
      { name: "Subscriptions", path: "/subscribe-list" }
    ]
  },

  {
    name: "Club Setup",
    icon: Trophy,
    children: [
      { name: "Club Setup", path: "/club-list" },
      { name: "Category Setup", path: "/category-list" },
      { name: "Court Setup", path: "/court-lists" },
      { name: "Schedule Court", path: "/schedule-court-list" }
    ]
  },

  {
    name: "Events / Tournaments",
    icon: Calendar,
    children: [
      { name: "Events", path: "/event-lists" },
      { name: "Club Event", path: "/social-events-lists" },
      { name: "Tournament Setup", path: "/list-tournament" },
      { name: "Tournament Type", path: "/club-event-tournament-list" },
      { name: "Club Team", path: "/club-event-team-list" }
    ]
  },

  {
    name: "Website Content",
    icon: Image,
    children: [
      { name: "Site Banner", path: "/site-banner" },
      { name: "Website Banner", path: "/website-banner-list" },
      { name: "Logo Setup", path: "/logo-lists" },
      { name: "Brochure Upload", path: "/brochure-list" },
      { name: "Application Forms", path: "/document-list" },
      { name: "Media Gallery", path: "/media-gallery-lists" },
      { name: "Photos", path: "/photo-gallery-lists" }
    ]
  },

  {
    name: "Content Pages",
    icon: Info,
    children: [
      { name: "About Us", path: "/about-list" },
      { name: "Our Values", path: "/our-values-list" },
      { name: "Privacy Policy", path: "/privacy-policy-list" },
      { name: "Terms & Conditions", path: "/terms-conditions-list" },
      { name: "Legal", path: "/legal-list" },
      { name: "Club Policy", path: "/policy-list" },
      { name: "Sport Section", path: "/sport-section-lists" },
      { name: "Programs Overview", path: "/program-overview-lists" },
      { name: "Pioneers, Exe., Board...", path: "/gallery-photo-list" },
      { name: "Coaches", path: "/coaches-list" },
      { name: "Guest Info", path: "/guest-list" },
      { name: "FAQ", path: "/faq-list" }
    ]
  },

  {
    name: "Extras",
    icon: Star,
    children: [
      { name: "Highlights", path: "/club-highlight" },
      { name: "Highlight Ext", path: "/club-highlight-ext" },
      { name: "Adverts Approval", path: "/advert-approval" },
      { name: "Membership Enquiry", path: "/contact-info" },
      { name: "Contact Us", path: "/contact-us" }
    ]
  },

  {
    name: "Audit",
    icon: ClipboardList,
    children: [
      { name: "Activity Logs", path: "/logs", icon: ClipboardList },
    ]
  },

  {
    name: "Permissions",
    icon: ClipboardList,
    children: [
      { name: "Create Role", path: "/create-role", icon: ClipboardList },
      { name: "Assign Role", path: "/assign-role", icon: ClipboardList },
      { name: "Create Route", path: "/create-route", icon: ClipboardList },
      { name: "Assign Route", path: "/assign-route", icon: ClipboardList },
    ]
  }
];

const memberMenu = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: House
  },

  {
    name: "Applications",
    icon: BookOpen,
    children: [
      { name: "My Applications", path: "/myapplications" },
      { name: "Sponsor Approval", path: "/applications-s" },
      { name: "Executive Approval", path: "/applications-e" }
    ]
  },

  {
    name: "Payments",
    icon: CreditCard,
    children: [
      { name: "My Payments", path: "/listpayments" }
    ]
  },

  {
    name: "Bookings",
    icon: Clock,
    children: [
      { name: "My Bookings", path: "/my-bookings" }
    ]
  },

  {
    name: "Adverts",
    path: "/advert-lists",
    icon: LifeBuoy
  },

  {
    name: "Profile",
    path: "/myprofile",
    icon: UsersRound
  }
];

const subadminMenu = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: House
  },

  {
    name: "Applications",
    icon: BookOpen,
    children: [
      { name: "My Applications", path: "/myapplications" },
      { name: "Sponsor Approval", path: "/applications-s" },
      { name: "Executive Approval", path: "/applications-e" }
    ]
  },

  {
    name: "Payments",
    icon: CreditCard,
    children: [
      { name: "My Payments", path: "/listpayments" }
    ]
  },

  {
    name: "Bookings",
    icon: Clock,
    children: [
      { name: "My Bookings", path: "/my-bookings" }
    ]
  },

  {
    name: "Adverts",
    path: "/advert-lists",
    icon: LifeBuoy
  },

  {
    name: "Profile",
    path: "/myprofile",
    icon: UsersRound
  }
];


const logout = [
{
    name: "Log out",
    icon: LogOut,
    action: () => {
     // Clear any hash from URL
     if (window.location.hash) {
        window.history.replaceState(null, null, window.location.pathname);
    }
    localStorage.removeItem("userid");
    localStorage.removeItem("username");
    localStorage.removeItem("email_address");
    localStorage.removeItem("user_role");
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("member_id");
    created_by.value = null;
    window.location.href = "/";
  }
  }
];

</script>

<style lang="scss" scoped>
.parent-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.parent-link .left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chevron {
  width: 16px;
  height: 16px;
  transition: transform 0.25s ease;
  opacity: 0.7;
}

.chevron.open {
  transform: rotate(180deg);
}

/* Group parent + submenu */
.menu-group {
  display: flex;
  flex-direction: column;
}

/* Parent spacing */
.parent-link {
  cursor: pointer;
}

/* Submenu alignment */
.submenu {
  margin-left: 36px; /* aligns with text, not icon */
  margin-top: 6px;
  padding-left: 0;
  overflow: hidden;
}

/* Submenu items */
.submenu-link {
  padding: 6px 8px;
  font-size: 10px;
  opacity: 0.85;
  border-radius: 6px;
}

/* Hover */
.submenu-link:hover {
  background-color: #3a3f44;
}

/* Transition */
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.25s ease;
}

.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.submenu-enter-to,
.submenu-leave-from {
  max-height: 400px;
  opacity: 1;
  transform: translateY(0);
}

/* Layout */
.dashboard {
    display: flex;
    height: 100vh;
    background: #0f172a;
    position: relative;
    padding: 0%;
    margin: 0%;
}

/* Sidebar */
.sidebar {
    background:
        linear-gradient(180deg, rgba(10, 23, 36, 0.98), rgba(15, 34, 24, 0.98)),
        #0f172a;
    width: 268px;
    min-width: 268px;
    border-right: 1px solid rgba(148, 163, 184, 0.16);
    transition: width 0.3s ease, transform 0.3s ease;
    display: flex;
    flex-direction: column;
    z-index: 1000;
    position: relative;
    overflow-y: auto;  /* shows scrollbar only when needed */
    overflow-x: hidden;
    max-height: 100vh; /* ensures the sidebar is scrollable within the viewport */
}

/* WebKit browsers (Chrome, Edge, Safari) */
.sidebar::-webkit-scrollbar {
  width: 6px;
}

.sidebar::-webkit-scrollbar-thumb {
  background-color: #555;
  border-radius: 4px;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background-color: #777;
}

/* Firefox */
.sidebar {
  scrollbar-width: thin;
  scrollbar-color: #555 #212529;
}


.sidebar.collapsed {
    width: 78px;
    min-width: 78px;
}

/* Mobile Sidebar */
.sidebar-mobile {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    transform: translateX(-100%);
}

.sidebar-mobile.sidebar-open {
    transform: translateX(0);
}

/* Backdrop for mobile */
.backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(2, 6, 23, 0.55);
    backdrop-filter: blur(2px);
    z-index: 900;
}

.mobile-toggle {
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 1100;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 999px;
    padding: 10px;
    border: none;
    cursor: pointer;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.category-title {
    color: #495057;
    font-size: 18px;
    margin: 0;
    padding: 5px;
    font-weight: 700;
    font-family: "Open Runde", sans-serif;
}

/* Sidebar header */
.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 14px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.sidebar-header h1 {
    font-size: 18px;
    margin: 0;
    color: #fff;
}

.toggle-container {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    padding: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: relative;
    right: -10px;
}

.toggle-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #fff;

}

.nav-link {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-decoration: none;
    color: #fff;
    padding: 11px 12px;
    width: 100%;
    margin-top: 10px;
    text-transform: none;
    border: 1px solid transparent;
    border-radius: 14px;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        transform 0.2s ease;
    
}

ul li {
    padding: 0%;
    margin: 0%;
}

.nav-link:hover {
    text-decoration: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.08);
    transform: translateX(2px);
}

.label {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    margin-top: 0;
    line-height: 1.3;
}

/* highlight active link */
.router-link-exact-active {
    background: linear-gradient(135deg, rgba(15, 91, 43, 0.95), rgba(8, 68, 33, 0.95));
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 12px 28px rgba(15, 91, 43, 0.28);
}

.router-link-exact-active .label {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
}

.router-link-exact-active .icon {
    color: #fff;
    /* highlight the icon too */
}

/* Navigation */
.sidebar-nav {
    margin-top: 12px;
    padding: 0 16px 18px;
    margin-left: 0%;
    margin-right: 0%;
    overflow-y: auto;
}


.sidebar-nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.nav-item {
    display: flex;
    align-items: center;
    padding: 0;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-bottom: 4px;
}

.icon {
    width: 18px;
    height: 18px;
    margin-right: 12px;
    color: #fff;
    flex-shrink: 0;
}

.icon-svg {
    width: 100%;
    height: 100%;
    stroke: #fff;
}

/* Main content */
.main-content {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    background:
        radial-gradient(circle at top right, rgba(15, 91, 43, 0.08), transparent 25%),
        linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%);
    color: #0f172a;
}

.main-content h2 {
    font-size: 24px;
    margin-bottom: 10px;
    color: #0f172a;
}

/* Optional smooth scrollbar */
.main-content::-webkit-scrollbar {
    width: 6px;
}

.main-content::-webkit-scrollbar-thumb {
    background-color: rgba(100, 100, 100, 0.3);
    border-radius: 3px;
}

@media (max-width: 768px) {
    .main-content {
        padding: 16px;
    }

    .toggle-container {
        right: 0;
        position: absolute;
        top: 12px;
        z-index: 1100;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 999px;
        padding: 8px;
    }
}
</style>

<style>
@import "@/assets/dashboard-skin.css";
</style>
